// src/Dashboard/pages/user/PassageCeintureDocuments/components/PassageCeintureDocumentDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  FaExternalLinkAlt,
  FaFilePdf,
  FaImage,
  FaSave,
  FaTimes,
  FaTrash,
  FaUpload,
} from "react-icons/fa";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  addPassageCeintureDocument,
  updatePassageCeintureDocument,
  uploadPassageCeintureImage,
  uploadPassageCeinturePdf,
  deletePassageCeintureImage,
  deletePassageCeinturePdf,
} from "../../../../../services/passageCeintureDocumentsServices";

import {
  isQuillEmpty,
  normalizePlainText,
  normalizeRichText,
  toAbsoluteUrl,
} from "../utils/passageCeintureDocumentsUtils";

const quillModules = {
  toolbar: [
    [{ header: [false, 3, 4] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
];

const emptyForm = {
  titre: "",
  description: "",
  ceinture: "",
  image_url: "",
  pdf_url: "",
  ordre_affichage: 0,
  is_published: true,
};

export default function PassageCeintureDocumentDrawer({
  isOpen,
  mode = "read",
  passageDocument,
  belts = [],
  canManage,
  onClose,
  onSaved,
}) {
  const isRead = mode === "read";
  const isCreate = mode === "create";
  const isEdit = mode === "edit" && !!passageDocument?.id;
  const canEditCurrent = canManage && !isRead;

  const [formData, setFormData] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const imageUrl = toAbsoluteUrl(
    isRead ? passageDocument?.image_url : formData.image_url
  );

  const pdfUrl = toAbsoluteUrl(
    isRead ? passageDocument?.pdf_url : formData.pdf_url
  );

  useEffect(() => {
    if (!isOpen) return;

    if (isCreate) {
      setFormData(emptyForm);
    } else {
      setFormData({
        titre: passageDocument?.titre || "",
        description: passageDocument?.description || "",
        ceinture: passageDocument?.ceinture || "",
        image_url: passageDocument?.image_url || "",
        pdf_url: passageDocument?.pdf_url || "",
        ordre_affichage: passageDocument?.ordre_affichage ?? 0,
        is_published:
          passageDocument?.is_published === undefined
            ? true
            : !!passageDocument.is_published,
      });
    }

    setImageFile(null);
    setPdfFile(null);
    setSubmitting(false);
  }, [isOpen, isCreate, passageDocument]);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = window.document.body.style.overflow;
    window.document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  const drawerTitle = useMemo(() => {
    if (isRead) return passageDocument?.titre || "Document";
    if (isEdit) return "Modifier le document";
    return "Ajouter un document";
  }, [isRead, isEdit, passageDocument]);

  if (!isOpen) return null;

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canManage) return;

    if (!normalizePlainText(formData.titre)) {
      toast.error("Le titre est obligatoire.");
      return;
    }

    if (!normalizePlainText(formData.ceinture)) {
      toast.error("La ceinture est obligatoire.");
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        titre: normalizePlainText(formData.titre),
        description: normalizeRichText(formData.description) || null,
        ceinture: normalizePlainText(formData.ceinture),
        image_url: normalizePlainText(formData.image_url) || null,
        pdf_url: normalizePlainText(formData.pdf_url) || null,
        ordre_affichage: Number(formData.ordre_affichage) || 0,
        is_published: !!formData.is_published,
      };

      const savedDocument = isEdit
        ? await updatePassageCeintureDocument(passageDocument.id, payload)
        : await addPassageCeintureDocument(payload);

      const targetId = savedDocument?.id || passageDocument?.id;

      if (imageFile && targetId) {
        await uploadPassageCeintureImage(targetId, imageFile);
      }

      if (pdfFile && targetId) {
        await uploadPassageCeinturePdf(targetId, pdfFile);
      }

      toast.success(isEdit ? "Document mis à jour." : "Document créé.");
      await onSaved?.();
    } catch (error) {
      console.error("Erreur enregistrement document :", error);
      toast.error("Erreur lors de l’enregistrement.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!passageDocument?.id) return;

    try {
      await deletePassageCeintureImage(passageDocument.id);
      toast.success("Image supprimée.");
      await onSaved?.();
    } catch (error) {
      console.error("Erreur suppression image :", error);
      toast.error("Erreur lors de la suppression de l’image.");
    }
  };

  const handleDeletePdf = async () => {
    if (!passageDocument?.id) return;

    try {
      await deletePassageCeinturePdf(passageDocument.id);
      toast.success("PDF supprimé.");
      await onSaved?.();
    } catch (error) {
      console.error("Erreur suppression PDF :", error);
      toast.error("Erreur lors de la suppression du PDF.");
    }
  };

  return (
    <div className="fixed inset-0 z-[110]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-5xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <header className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-4 dark:border-slate-800 sm:px-6">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
              Passage de ceinture
            </p>

            <h2 className="truncate text-lg font-bold text-slate-900 dark:text-white sm:text-xl">
              {drawerTitle}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            <FaTimes />
          </button>
        </header>

        {isRead ? (
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
              <div className="space-y-5">
                <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={passageDocument?.titre || "Document"}
                      className="max-h-[560px] w-full object-contain"
                      onError={(event) => {
                        event.currentTarget.src = "/img/no-image.jpg";
                      }}
                    />
                  ) : (
                    <div className="flex h-72 items-center justify-center text-slate-400">
                      <FaImage className="text-5xl" />
                    </div>
                  )}
                </div>

                {!isQuillEmpty(passageDocument?.description) && (
                  <div className="rounded-[24px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950">
                    <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Description
                    </h3>

                    <div
                      className="prose prose-sm mt-4 max-w-none text-slate-700 dark:prose-invert dark:text-slate-200"
                      dangerouslySetInnerHTML={{
                        __html: passageDocument.description,
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="rounded-[24px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Ceinture
                  </p>

                  <p className="mt-2 text-lg font-bold text-slate-900 dark:text-white">
                    {passageDocument?.ceinture || "—"}
                  </p>

                  {canManage && (
                    <div className="mt-4">
                      <span
                        className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${
                          passageDocument?.is_published
                            ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
                            : "bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20"
                        }`}
                      >
                        {passageDocument?.is_published ? "Publié" : "Brouillon"}
                      </span>
                    </div>
                  )}
                </div>

                {pdfUrl && (
                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center gap-3 rounded-[24px] bg-red-50 px-5 py-4 text-sm font-bold text-red-700 ring-1 ring-red-200 transition hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20"
                  >
                    <FaFilePdf className="text-xl" />
                    Ouvrir le PDF
                    <FaExternalLinkAlt className="text-xs opacity-70" />
                  </a>
                )}
              </div>
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex flex-1 flex-col overflow-hidden"
          >
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
                <div className="space-y-4">
                  <div>
                    <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                      Titre *
                    </label>

                    <input
                      type="text"
                      name="titre"
                      value={formData.titre}
                      onChange={handleChange}
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                      Ceinture *
                    </label>

                    <select
                      name="ceinture"
                      value={formData.ceinture}
                      onChange={handleChange}
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    >
                      <option value="">Sélectionner une ceinture</option>

                      {belts.map((belt) => (
                        <option key={belt} value={belt}>
                          {belt}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                      Description
                    </label>

                    <div className="overflow-hidden rounded-2xl border border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-950">
                      <ReactQuill
                        theme="snow"
                        value={formData.description}
                        onChange={(value) =>
                          setFormData((previous) => ({
                            ...previous,
                            description: value,
                          }))
                        }
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Description, consignes, objectifs..."
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                        Ordre d’affichage
                      </label>

                      <input
                        type="number"
                        min="0"
                        name="ordre_affichage"
                        value={formData.ordre_affichage}
                        onChange={handleChange}
                        className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                      />
                    </div>

                    <div className="flex items-end">
                      <label className="flex w-full cursor-pointer items-center gap-3 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-bold text-slate-800 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100">
                        <input
                          type="checkbox"
                          name="is_published"
                          checked={!!formData.is_published}
                          onChange={handleChange}
                        />
                        Publié
                      </label>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                    <div className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                      <FaImage />
                      Image
                    </div>

                    <input
                      type="text"
                      name="image_url"
                      value={formData.image_url}
                      onChange={handleChange}
                      placeholder="URL image externe ou /uploads/..."
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                    />

                    <label className="mt-3 flex cursor-pointer items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-5 text-sm font-bold text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800">
                      <FaUpload />
                      {imageFile ? imageFile.name : "Uploader une image"}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={(event) =>
                          setImageFile(event.target.files?.[0] || null)
                        }
                        className="hidden"
                      />
                    </label>

                    {imageUrl && (
                      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                        <img
                          src={imageUrl}
                          alt="Aperçu"
                          className="max-h-56 w-full object-contain"
                          onError={(event) => {
                            event.currentTarget.src = "/img/no-image.jpg";
                          }}
                        />
                      </div>
                    )}

                    {isEdit && passageDocument?.image_url && (
                      <button
                        type="button"
                        onClick={handleDeleteImage}
                        className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-red-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-red-700"
                      >
                        <FaTrash />
                        Supprimer l’image
                      </button>
                    )}
                  </div>

                  <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                    <div className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                      <FaFilePdf />
                      PDF
                    </div>

                    <input
                      type="text"
                      name="pdf_url"
                      value={formData.pdf_url}
                      onChange={handleChange}
                      placeholder="URL PDF externe ou /uploads/..."
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                    />

                    <label className="mt-3 flex cursor-pointer items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-5 text-sm font-bold text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800">
                      <FaUpload />
                      {pdfFile ? pdfFile.name : "Uploader un PDF"}
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={(event) =>
                          setPdfFile(event.target.files?.[0] || null)
                        }
                        className="hidden"
                      />
                    </label>

                    {pdfUrl && (
                      <a
                        href={pdfUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-4 flex items-center justify-center gap-2 rounded-2xl bg-red-50 px-4 py-3 text-sm font-bold text-red-700 ring-1 ring-red-200 transition hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20"
                      >
                        <FaFilePdf />
                        Voir le PDF
                        <FaExternalLinkAlt className="text-xs opacity-70" />
                      </a>
                    )}

                    {isEdit && passageDocument?.pdf_url && (
                      <button
                        type="button"
                        onClick={handleDeletePdf}
                        className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-red-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-red-700"
                      >
                        <FaTrash />
                        Supprimer le PDF
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <footer className="border-t border-slate-200 bg-white/95 p-4 dark:border-slate-800 dark:bg-slate-900/95">
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <FaTimes />
                  Annuler
                </button>

                <button
                  type="submit"
                  disabled={submitting || !canEditCurrent}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaSave />
                  {submitting ? "Enregistrement..." : "Enregistrer"}
                </button>
              </div>
            </footer>
          </form>
        )}
      </aside>
    </div>
  );
}